import { type FC, useEffect, useCallback, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import Player from '@vimeo/player';
import styles from './styles/SellerApplicationDescription.scss';
import ApplyNowButton from './form/ApplyNowButton.tsx';

const vimeoId = '622997017';
const messages = defineMessages({
    h1: { id: 'DealerApplicationWrapper.h1', defaultMessage: 'We invite you to join us' },
    h2: {
        id: 'DealerApplicationDescription.h2',
        defaultMessage: 'Together, we bring beautiful things to millions',
    },
    text1: {
        id: 'DealerApplicationDescription.text1',
        defaultMessage:
            '1stDibs is a leading online marketplace for extraordinary design. With uniquely curated shopping experiences, our website and mobile app are go-to destinations for discerning collectors, sophisticated customers, and top interior designers around the world.',
    },
    text2: {
        id: 'DealerApplicationDescription.text2',
        defaultMessage:
            'By bringing together more than 7,600+ of the world’s best sellers and galleries from over 50 countries specializing in furniture, fine art, jewelry, and collectibles, our global e-commerce platform offers a unique collection of inventory for buyers seeking the most beautiful things on earth.',
    },
    text3: {
        id: 'DealerApplicationDescription.text3',
        defaultMessage:
            'We select only prestigious sellers and galleries to bring their merchandise to this highly targeted, global audience.',
    },
});

type Props = { handleApplyNow: () => void; showForm: boolean; isMobile: boolean };

const Description: FC<Props> = ({ handleApplyNow, showForm, isMobile }) => {
    const intl = useIntl();
    const [playedVideo, setPlayedVideo] = useState(false);
    const playerRef = useRef<Player | null>(null);
    const videoRef = useRef(null);

    const handleInteraction = useCallback((): void => {
        if (playerRef.current) {
            playerRef.current?.setVolume(1);
            playerRef.current?.play();
            setPlayedVideo(true);
        }
    }, [setPlayedVideo]);

    useEffect(() => {
        const cleanUp = (): void => {
            document.body.removeEventListener('keydown', handleInteraction);
            document.body.removeEventListener('click', handleInteraction);
            document.body.removeEventListener('touchstart', handleInteraction);
        };

        if (videoRef.current && !playerRef.current) {
            playerRef.current = new Player(videoRef?.current || vimeoId, { controls: false });
            document.body.addEventListener('keydown', handleInteraction, { once: true });
            document.body.addEventListener('click', handleInteraction, { once: true });
            document.body.addEventListener('touchstart', handleInteraction, { once: true });
        }
        if (playedVideo) {
            //clear once the video was played to avoid playing video twice
            cleanUp();
        }
        return cleanUp; // make sure no event listeners are left on unmount
    }, [videoRef, playerRef, handleInteraction, playedVideo]);

    return (
        <div className={styles.wrapper}>
            <h1 className={styles.heading}>{intl.formatMessage(messages.h1)}</h1>
            <h2 className={styles.subHeading}>{intl.formatMessage(messages.h2)}</h2>
            <iframe
                ref={videoRef}
                className={styles.bannerVideo}
                title="Application video"
                src="https://player.vimeo.com/video/622997017?h=73c295644a"
                frameBorder="0"
                allowFullScreen
            />
            <div className={styles.textWrapper}>
                <p className={styles.text}>{intl.formatMessage(messages.text1)}</p>
                <p className={styles.text}>{intl.formatMessage(messages.text2)}</p>
                <p className={styles.text}>{intl.formatMessage(messages.text3)}</p>
                {!showForm && (
                    <ApplyNowButton
                        className={styles.cta}
                        size={isMobile ? 'small' : 'large'}
                        onClick={handleApplyNow}
                    />
                )}
            </div>
        </div>
    );
};
export const SellerApplicationDescription = Description;
